'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import { useT } from '@repo/i18n/components/i18n.client';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentMapInfo, selectFoundPins, selectIsCategoryHidden, selectMapConfig, selectMapData, selectSelectedLabelId, selectSelectedMarkerId, selectSelectedRegionId, toggleCategoryFilter } from '@repo/store/maps/slices/mapData';
import { selectSelectedMapObject3dId } from '@repo/store/maps/slices/mapObjects3d';
import { createPublicMapUrl } from '@repo/types/maps/map-utils';
import { copyTextToClipboard } from '@repo/utility/clipboard';
import { useCallback, useMemo } from 'react';
import { use3dObjectsLookupMap } from '../../game-map/hooks/use3dObjectsLookupMap';
import { useMapPinOperations } from '../../game-map/hooks/useMapPinOperations';
export function useLocationInfo() {
  const dispatch = useAppDispatch();
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const selectedLabelId = useAppSelector(selectSelectedLabelId);
  const selectedRegionId = useAppSelector(selectSelectedRegionId);
  const selected3dObjectId = useAppSelector(selectSelectedMapObject3dId);
  const mapData = useAppSelector(selectMapData);
  const foundPins = useAppSelector(selectFoundPins);
  const locationSubcategories = useAppSelector(state => state.mapData.locationSubcategories);
  const locationCategories = useAppSelector(state_0 => state_0.mapData.locationCategories);
  const mapConfig = useAppSelector(selectMapConfig);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const {
    t
  } = useT(['game_map', 'common']);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const {
    togglePinFound,
    isTogglingPin
  } = useMapPinOperations();
  const objects3dLookupMap = use3dObjectsLookupMap(mapData);
  const selectedLocation = useMemo(() => {
    if (!selectedMarkerId || !mapData) return null;
    return mapData.geojson.features.find(feature => feature.properties.externalId === selectedMarkerId) ?? null;
  }, [selectedMarkerId, mapData]);
  const selectedLabel = useMemo(() => {
    if (!selectedLabelId || !mapData) return null;
    return mapData.labels?.features.find(label => label.properties.externalId === selectedLabelId) ?? null;
  }, [selectedLabelId, mapData]);
  const selectedRegion = useMemo(() => {
    if (!selectedRegionId || !mapData) return null;
    return mapData.regions?.features.find(region => region.properties.externalId === selectedRegionId) ?? null;
  }, [selectedRegionId, mapData]);
  const selected3dObject = useMemo(() => {
    if (!selected3dObjectId) return null;
    return objects3dLookupMap.get(selected3dObjectId) ?? null;
  }, [selected3dObjectId, objects3dLookupMap]);
  const subcategory = useMemo(() => {
    if (!selectedLocation) return null;
    return locationSubcategories[selectedLocation.properties.externalId] || null;
  }, [selectedLocation, locationSubcategories]);
  const category = useMemo(() => {
    if (!selectedLocation) return null;
    return locationCategories[selectedLocation.properties.externalId] || null;
  }, [selectedLocation, locationCategories]);
  const isCategoryHidden = useAppSelector(selectIsCategoryHidden(selectedLocation?.properties.isCustomPin ? 'custompins' : category?.externalId || ''));
  const isFound = selectedLocation ? foundPins[selectedLocation.properties.externalId] || false : false;
  const handleToggleFound = useCallback(async () => {
    if (!selectedLocation) return;
    await togglePinFound(selectedLocation.properties.externalId, 'location-info');
  }, [selectedLocation, togglePinFound]);
  const handleShare = useCallback(async () => {
    const isCustomPin = selectedLocation?.properties.isCustomPin;
    const shareUrl = createPublicMapUrl({
      titleSlug: mapConfig?.titleSlug,
      mapSlug: currentMapInfo?.slug,
      location: selectedLocation?.properties.externalId,
      ...(isCustomPin && {
        isCustomPin: 'true'
      })
    });
    const result = await copyTextToClipboard(shareUrl);
    if (result.success) {
      toast({
        title: isCustomPin ? t($ => $.custom_pin_share_copied) : t($_0 => $_0.share_link_copied_to_clipboard, {
          ns: 'common'
        }),
        variant: 'success'
      });
    } else {
      toast({
        title: t($_1 => $_1.copy_error, {
          ns: 'common'
        }),
        variant: 'error'
      });
    }
    if (currentMapInfo?.externalId && selectedLocation?.properties.externalId) {
      analytics.event('game_map_location_info_share_link_click', {
        mapId: currentMapInfo.externalId,
        locationId: selectedLocation.properties.externalId,
        platform: hostType,
        mapName: mapData?.metadata.name ?? '',
        titleName: mapConfig?.titleName ?? ''
      });
    }
  }, [selectedLocation, mapConfig, currentMapInfo, t, analytics, hostType, mapData]);
  const handleLocationTeleport = async () => {
    if (!selectedLocation?.properties?.gameCoords) return;
    try {
      const success = await getHost().setPlayerCoordinates(selectedLocation.properties.gameCoords);
      if (analytics && currentMapInfo?.externalId && hostType && selectedLocation?.properties.externalId) {
        analytics.event('game_map_teleport_click', {
          mapId: currentMapInfo.externalId,
          locationId: selectedLocation.properties.externalId,
          success,
          platform: hostType
        });
      }
      if (!success) {
        toast({
          title: t($_3 => $_3.set_player_coordinates_error),
          variant: 'error'
        });
      }
    } catch {
      toast({
        title: t($_2 => $_2.set_player_coordinates_error),
        variant: 'error'
      });
    }
  };
  const handleToggleCategoryVisibility = useCallback(() => {
    if (selectedLocation?.properties.isCustomPin) {
      dispatch(toggleCategoryFilter('custompins'));
    } else if (category) {
      dispatch(toggleCategoryFilter(category.externalId));
    }
    analytics.event('game_map_toggle_category_visibility', {
      mapId: currentMapInfo?.externalId ?? '',
      categoryId: category?.externalId ?? '',
      mapName: mapData?.metadata.name ?? '',
      titleName: mapConfig?.titleName ?? '',
      platform: hostType,
      categoryName: category?.title ?? '',
      hidden: !isCategoryHidden
    });
  }, [selectedLocation, category, dispatch, analytics, currentMapInfo, hostType, mapData, mapConfig, isCategoryHidden]);
  return {
    selectedLocation,
    selectedLabel,
    selectedRegion,
    selected3dObject,
    subcategory,
    category,
    isFound,
    isTogglingPin,
    isCategoryHidden,
    handleToggleFound,
    handleShare,
    handleToggleCategoryVisibility,
    handleLocationTeleport
  };
}